import { Maybe, nothing } from 'maybeasy';
import { Error, error } from '../ErrorHandling';
import { Resource } from '../Resource/Types';
import { TPlainTextKey } from '../Translations';

export interface PusherSettings {
  kind: 'pusher-settings';
  key: string;
  cluster: string;
}

export type ToolSettings = PusherSettings;

export interface User {
  id: number;
  platformUserId: Maybe<number>;
  name: Maybe<string>;
  email: string;
  initials: string;
  shortName: string;
  organizationId: number;
  organizationAccountUID: string;
  organizationName: string;
  contactUID: Maybe<string>;
  exoOrg: boolean;
  organizationPlatformOrgUID: string;
  productLicenseIds: number[];
  productLicenseTypeIds: number[];
  epPrivilegeIds: number[];
  inactivityTimeoutMinutes: number;
  createdAt: Date;
  exoChatbotAccessible: boolean;
  loginType: string;
  enrollmentDetails: string[];
  userProductLicenseDetails: string[];
}

export type UserResource = Resource<User>;

export const sessionExpiredMessage: TPlainTextKey = 'Your session has expired. Please login.';

export type State =
  | Waiting
  | Identified
  | Identifying
  | LoggingOut
  | Anonymous
  | Unauthorized
  | Errored;

export type Authenticated = Identified | LoggingOut;

export interface Waiting {
  kind: 'waiting';
}

export interface Identified {
  kind: 'identified';
  currentUserResource: UserResource;
}

export interface Identifying {
  kind: 'identifying';
}

export interface LoggingOut {
  kind: 'logging-out';
  currentUserResource: UserResource;
}

export interface Anonymous {
  kind: 'anonymous';
  error: Maybe<Error>;
  email: Maybe<string>;
}

export interface Unauthorized {
  kind: 'unauthorized';
  error: Error;
}

export interface Errored {
  kind: 'errored';
  error: Error;
}

export const waiting = (): Waiting => ({
  kind: 'waiting',
});

export const identified = (currentUserResource: UserResource): Identified => ({
  kind: 'identified',
  currentUserResource,
});

export const identifying = (): Identifying => ({
  kind: 'identifying',
});

export const loggingOut = (currentUserResource: UserResource): LoggingOut => ({
  kind: 'logging-out',
  currentUserResource,
});

export const anonymous = (failureMessage: Maybe<TPlainTextKey>): Anonymous => ({
  kind: 'anonymous',
  error: failureMessage.map(error),
  email: nothing(),
});

export const unauthorized = (failureMessage: TPlainTextKey): Unauthorized => ({
  kind: 'unauthorized',
  error: error(failureMessage),
});

export const errored = (failureMessage: TPlainTextKey): Errored => ({
  kind: 'errored',
  error: error(failureMessage),
});
